import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import React from 'react'

import {
  allocateAptInventory,
  consumeAptInventory,
  deleteAptInventoryItem,
  fetchAptCatalogue,
  fetchAptInventory,
  saveAptStockCheck
} from 'api/apartmentProcurement'
import { notify } from 'app/helpers'

import { RoleContext } from './roleContext'
import useColumnBands, { bandHeaderRow, mapBandedCells } from './useColumnBands'
import useSegmentGroups, { groupEntries } from './useSegmentGroups'

// Full per-apartment inventory (the HQ store is one location among all). Delivered
// lines stock into their apartment; stock is transferred between locations and
// used/consumed is logged — on-hand = received + transfers-in − transfers-out −
// consumed (extended 2026-07-27; was HQ-only).
const REASON_LABEL = {
  receipt: 'Received',
  transfer: 'Transfer',
  consumption: 'Used'
}

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(1) },
  lede: { color: theme.palette.text.secondary, marginBottom: theme.spacing(2), maxWidth: '74ch' },
  bar: { display: 'flex', alignItems: 'center', gap: theme.spacing(2), flexWrap: 'wrap', marginBottom: theme.spacing(1) },
  picker: { minWidth: 260 },
  toggle: { display: 'inline-flex', border: '1px solid #e0e0e0', borderRadius: 4, overflow: 'hidden' },
  h: { fontWeight: 700, margin: `${theme.spacing(2)}px 0 ${theme.spacing(1)}px` },
  num: { fontVariantNumeric: 'tabular-nums' },
  low: { color: theme.palette.text.secondary },
  // ONE header row, and the search box IS the header: the column name is the
  // input's placeholder, not a label stacked above it — a label plus an input
  // reads as two rows even when it is one <tr>. Columns with nothing to search
  // just print their name, bottom-aligned so they sit on the same line as the
  // inputs. Same shape as the requests grid and the daily sheet (2026-08-07).
  headCell: { verticalAlign: 'bottom', whiteSpace: 'nowrap' },
  headLabel: {
    display: 'block',
    fontSize: 11,
    fontWeight: 700,
    color: theme.palette.text.secondary,
    marginBottom: 2
  },
  headInput: {
    '& input': { fontSize: 12, padding: '4px 6px' },
    '& input::placeholder': { color: theme.palette.text.disabled, opacity: 1 }
  },
  // Segment and Item stay on screen while the rest scrolls sideways. Both carry
  // an explicit width — a sticky offset computed from variable-width columns is
  // what made the daily sheet's attempt at this flaky.
  freeze: {
    position: 'sticky',
    zIndex: 3,
    background: theme.palette.background.paper
  },
  // A sticky cell needs its own opaque background or the scrolling content
  // shows through — but that leaves the frozen columns white while the rest of
  // the row is striped. Paint the stripe on the CELLS, as HqMonthlyBoard does,
  // so the frozen columns carry the same band. Hover stays last: equal
  // specificity, so source order wins.
  striped: {
    '& tbody tr:nth-of-type(odd) td': { backgroundColor: theme.palette.grey[50] },
    '& tbody tr:nth-of-type(even) td': {
      backgroundColor: theme.palette.background.paper
    },
    '& tbody tr:hover td': { backgroundColor: theme.palette.action.hover }
  },
  // Segment and item wrap onto a second line rather than running wide — they
  // were eating close to half the table between them.
  wrapCell: {
    display: 'block',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    lineHeight: 1.25
  },
  freezeEdge: { borderRight: `2px solid ${theme.palette.divider}` },
  // ⛔ Category absorbs the table's slack, so with a location picked (four more
  // columns) it is squeezed to its floor and "5+ Public Speaking" ran straight
  // over the Type column beside it. It wraps, exactly like Segment and Item
  // above — NOT an ellipsis, which hid the tail of a reference id in a column
  // wide enough to hold it.
  // The corner cells are sticky on BOTH axes, so they need `top` as well as
  // `left`, and a z-index above the header (5) and the frozen body cells (3).
  freezeHead: { top: 0, zIndex: 7 },
  gapPill: {
    fontSize: 10.5,
    fontWeight: 700,
    padding: '2px 7px',
    borderRadius: 999,
    whiteSpace: 'nowrap'
  },
  pillOk: { background: '#e7f2ec', color: '#2f7d5d' },
  pillShort: { background: '#fbeae7', color: '#b03a2e' },
  pillNone: { background: '#fbf1de', color: '#9a6414' },
  linkArrow: { marginLeft: 4, textDecoration: 'none' },
  // ------------------------------------------------ organised like the requests grid
  // The same two devices the At Apartment requests grid uses (2026-08-26 /
  // 09-02): fold a run of columns away by its orange band header, and group the
  // rows by segment. Same look, same words, same localStorage shape — the two
  // tabs are read by the same people on the same day, so a device that means
  // one thing on one of them has to mean it on the other.
  bandChip: { textTransform: 'none', fontSize: 12, padding: '2px 8px', minWidth: 0 },
  bandCell: {
    background: '#fdefea !important',
    color: '#f25e35',
    fontWeight: 700,
    borderBottom: '2px solid #f25e35 !important',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    '&:hover': { background: '#f9ddd2 !important' }
  },
  bandRowSpacer: { cursor: 'default !important', background: 'transparent' },
  // A folded band is a 10px seam, not a column — see the note on the requests
  // grid: at 26px it read as a broken empty channel through every row.
  stubCell: {
    width: 10,
    minWidth: 10,
    maxWidth: 10,
    padding: '0 !important',
    textAlign: 'center',
    cursor: 'pointer',
    fontSize: 9,
    lineHeight: 1,
    color: '#f25e35',
    background: '#fdefea'
  },
  stubBodyCell: {
    width: 10,
    minWidth: 10,
    maxWidth: 10,
    padding: '0 !important',
    background: 'inherit',
    borderRight: '1px solid #f0ddd4'
  },
  stubText: { fontSize: 11, fontWeight: 700, lineHeight: 1 },
  // Segment header row. ⛔ The tint has to be painted on the CELLS, not the
  // row: `striped` above already paints every td, and a row-level background
  // loses to it — the group header would render as an ordinary striped row.
  groupRow: {
    cursor: 'pointer',
    '& td': {
      background: '#f7f4ee !important',
      fontWeight: 600,
      borderTop: '1px solid #e8e4dc'
    },
    '&:hover td': { background: '#f1ece2 !important' }
  },
  groupMeta: { color: '#8a857c', fontWeight: 400, fontSize: 11, marginLeft: 7 },
  groupCount: { fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  lastCount: {
    marginLeft: 10,
    fontWeight: 400,
    fontSize: 11.5,
    color: '#8a857c'
  },
  // The count entry cell. Deliberately as wide as its column and no wider: a
  // person types 372 of these and every extra pixel of chrome is in the way.
  countInput: {
    '& input': {
      fontSize: 13,
      padding: '5px 6px',
      textAlign: 'right',
      fontVariantNumeric: 'tabular-nums'
    }
  },
  diffUp: { color: '#2f7d5d', fontWeight: 700 },
  diffDown: { color: '#b03a2e', fontWeight: 700 },
  diffSame: { color: '#8a857c' },
  // The save bar rides the bottom of the window, because the shelf is walked
  // top to bottom and the button cannot be 372 rows behind you.
  countBar: {
    position: 'sticky',
    bottom: 0,
    zIndex: 8,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
    padding: '10px 12px',
    marginTop: 8,
    background: '#FBF5ED',
    borderTop: '2px solid #F25E35'
  }
}))

// Every column's width, in one place. The table is laid out `fixed` and carries
// a <colgroup> built from these, because in the default `auto` layout a table
// cell's `width`/`max-width` are only hints: with a location picked the table
// was constrained and the columns did land on their declared sizes, but on
// "all apartments" there are fewer columns, the browser had slack to
// distribute, and segment/item ballooned to ~340px and ~450px. That threw the
// sticky offsets — which are derived from these same numbers — so the freeze
// silently stopped lining up. Fixed layout makes the declared width the real
// one in both cases.
// Category is deliberately left OUT of the colgroup. Under `table-layout:
// fixed`, columns that declare a width get exactly it and the ones that don't
// share whatever is left — so category alone absorbs the slack, the table fills
// its container instead of stopping short in a sea of white, and the frozen
// columns still land on the exact widths the sticky offsets are computed from.
const CB_W = 42
const SEG_W = 150
const ITEM_W = 210
const STUB_W = 10
const COL_W = {
  reference_id: 130,
  category: 120,
  item_type: 80,
  unit: 60,
  required: 80,
  on_hand: 80,
  status: 110,
  actions: 250,
  counted: 100,
  difference: 100,
  count_reason: 180
}

// The columns after the frozen Segment/Item pair, in order. Segment and Item
// are NOT in here: they are sticky, they carry their own geometry, and they can
// never be folded away — a row with neither is unreadable.
//   ph      the placeholder for the column's own search box (no ph = a plain label)
//   flex    the column that absorbs the table's slack (see the colgroup note)
//   locOnly only meaningful for one location, so it is dropped on "all"
const INV_COLS = [
  { k: 'reference_id', label: 'Reference ID', ph: 'reference id' },
  { k: 'category', label: 'Category', ph: 'category', flex: true },
  { k: 'item_type', label: 'Type', ph: 'type' },
  { k: 'unit', label: 'Unit' },
  { k: 'required', label: 'Required', num: true, locOnly: true },
  { k: 'on_hand', label: 'On hand', num: true },
  { k: 'status', label: 'Status', locOnly: true },
  { k: 'actions', label: 'Actions', locOnly: true }
]

// The stock-check column set. It keeps the same Item detail band at the head —
// the band is the shared thing between the two, so folding it away in one mode
// and coming back to the other finds it folded, which is the point.
// ⛔ `on_hand` is labelled "Alfred says", not "On hand": in this mode the two
// numbers sit side by side and the person has to know which one is the claim
// they are checking and which one is theirs.
const COUNT_COLS = [
  { k: 'reference_id', label: 'Reference ID', ph: 'reference id' },
  { k: 'category', label: 'Category', ph: 'category', flex: true },
  { k: 'item_type', label: 'Type', ph: 'type' },
  { k: 'unit', label: 'Unit' },
  { k: 'required', label: 'Required', num: true },
  { k: 'on_hand', label: 'Alfred says', num: true },
  { k: 'counted', label: 'Counted', num: true },
  { k: 'difference', label: 'Difference', num: true },
  { k: 'count_reason', label: 'Reason' }
]

// A number that moves needs one of these. Kept short and concrete: a free-text
// box on 372 rows gets "-" typed into it, and the whole value of the reason is
// that the difference can be read back months later.
const COUNT_REASONS = [
  'Damaged',
  'Lost / missing',
  'Never arrived',
  'Found extra',
  'Counted wrong earlier',
  'Used, not recorded'
]

// ⛔ A band MUST be a CONTIGUOUS run of INV_COLS — the band header renders with
// a colSpan over the run, so a gap mis-aligns the whole header row.
// One band, not four: this grid has ten columns where the requests grid has
// twenty-five. Folding `Item detail` away leaves Segment · Item · Required ·
// On hand · Status — which IS the count sheet a person walks the shelf with.
const INV_BANDS = [
  {
    id: 'detail',
    label: 'Item detail',
    short: 'DETAIL',
    cols: ['reference_id', 'category', 'item_type', 'unit']
  }
]

// Per-browser preferences. Never shared, never sent anywhere — they only decide
// how this grid draws.
const BANDS_KEY = 'aptInventory.foldedBands'
const GROUP_KEY = 'aptInventory.groupBySegment'

const InventoryTab = () => {
  const classes = useStyles()
  const [scope, setScope] = React.useState('all')
  const [data, setData] = React.useState(null)
  const [loading, setLoading] = React.useState(false)
  const [busy, setBusy] = React.useState(false)
  const [send, setSend] = React.useState(null) // {item, qty, to, error}
  const [use, setUse] = React.useState(null) // {item, qty, note, error}
  const [view, setView] = React.useState('stock') // 'stock' | 'count' | 'log'
  // The stock check in progress: item_key -> what was counted / why it moved.
  // Held in the tab, not per row, so ONE Save posts the whole walk of the shelf
  // — which is what a stock check is. Nothing is written until then.
  const [counts, setCounts] = React.useState({})
  const [reasons, setReasons] = React.useState({})
  const [countNote, setCountNote] = React.useState('')
  const [countErr, setCountErr] = React.useState('')
  // Category is searched in its own header cell like every other column — the
  // toolbar dropdown that used to do it was a second place to look for the same
  // thing (2026-08-07).
  const [colFilters, setColFilters] = React.useState({
    segment: '',
    item_name: '',
    reference_id: '',
    category: '',
    item_type: ''
  })
  // Every approved catalogue item is a row, whether or not stock ever moved —
  // an apartment that never received something has to be able to see that.
  const [catalogue, setCatalogue] = React.useState([])
  const [gapsOnly, setGapsOnly] = React.useState(false)
  // OFF by default (Satya, 2026-09-17: "by default something is selected, can
  // we make it all unselected"). It was ON from 2026-08-09 so a location opened
  // on what it holds rather than hundreds of zero rows; now the tab opens with
  // no filter on and the toggle narrows to what is held. The empty state still
  // offers the rest of the catalogue for when it is switched on.
  const [stockOnly, setStockOnly] = React.useState(false)
  const [del, setDel] = React.useState(null) // {item} pending scrap-delete
  const [bsel, setBsel] = React.useState(() => new Set()) // bulk-transfer selection
  const [bulkOpen, setBulkOpen] = React.useState(false)
  const [bulkTo, setBulkTo] = React.useState('') // bulk-transfer destination
  const role = React.useContext(RoleContext)
  // Super admin or the At Apartment head (Central Management) can scrap.
  const canScrap = !!(role && (role.can_scrap_inventory || role.is_super_admin))

  // ⭐ Both hooks hold STATE ONLY and must sit above anything conditional —
  // `groupEntries` and the display sequence are plain functions used further
  // down, where the rows actually exist. Same split as the requests grid.
  const isAll = scope === 'all'
  // A stock check is always AT somewhere — "all apartments" is a rollup and
  // there is no shelf to walk.
  const counting = view === 'count' && !isAll
  const gridCols = React.useMemo(
    () => (counting ? COUNT_COLS : INV_COLS.filter((c) => !c.locOnly || !isAll)),
    [isAll, counting]
  )
  const { displayCols, toggleBand, bandOf } = useColumnBands(
    gridCols,
    BANDS_KEY,
    [],
    0,
    INV_BANDS
  )
  const {
    grouped,
    toggleGrouped,
    collapsed: collapsedGroups,
    toggleGroup,
    expandAll,
    collapseAllOf
  } = useSegmentGroups(GROUP_KEY)

  const load = React.useCallback((sc) => {
    setLoading(true)
    fetchAptInventory(sc)
      .catch(() => ({ scope: sc, locations: [], items: [], ledger: [] }))
      .then(setData)
      .finally(() => setLoading(false))
  }, [])
  React.useEffect(() => load(scope), [load, scope])

  // The catalogue is the same for every location, so it is fetched once.
  React.useEffect(() => {
    fetchAptCatalogue()
      .then((r) => setCatalogue((r && (r.results || r.items || r)) || []))
      .catch(() => setCatalogue([]))
  }, [])

  const locations = (data && data.locations) || []
  const allItems = (data && data.items) || []
  // Stock identifies an item three ways and the API does not fill the same one
  // every time: `item_key` is what the movement stored (a reference id when the
  // item had one, else the name), while `reference_id` is only populated when
  // the stock arrived on a delivered line — most rows leave it empty. Matching
  // on reference-id-then-name joined 1 of 147 stock rows on a real database;
  // every other one fell through and rendered as an orphan, so its on-hand
  // never reached the catalogue row it belonged to. Index stock under all three
  // and look the catalogue up against that.
  const norm = (v) => String(v || '').trim().toLowerCase()

  // Catalogue first, so the list reads in catalogue order and a never-received
  // item still gets a row. Anything stocked that is not in the catalogue (older
  // ad-hoc deliveries) is appended rather than hidden.
  const liveCatalogue = catalogue.filter(
    (c) => c.status === 'approved' || c.status === 'base'
  )
  const stockIndex = new Map()
  allItems.forEach((s) => {
    ;[s.item_key, s.reference_id, s.item_name].forEach((id) => {
      const n = norm(id)
      if (n && !stockIndex.has(n)) stockIndex.set(n, s)
    })
  })
  // A stock row belongs to exactly ONE catalogue row. Two catalogue items can
  // share a name and differ only by reference id — "Easy cards" exists as both
  // AA_5P_154 and AA_35_067 — and the stock sits under one of them. Matching in
  // a single pass let the reference-id owner AND the same-named sibling both
  // claim it, and both then rendered its 20 on hand: the tab read 546 units
  // against a real 506. So claim by reference id first, then let only the
  // still-unclaimed rows match by name, and a stock row already spoken for is
  // not offered twice.
  const matched = new Set()
  const claim = (c, byName) => {
    const s = byName
      ? stockIndex.get(norm(c.item_name))
      : stockIndex.get(norm(c.reference_id))
    if (!s || !s.item_key || matched.has(s.item_key)) return null
    matched.add(s.item_key)
    return s
  }
  // Pass 1 is reference id, which is the identifier the stock was actually
  // filed under; pass 2 is the looser name match for whatever is left.
  const claimed = new Map()
  liveCatalogue.forEach((c) => {
    const s = claim(c, false)
    if (s) claimed.set(c, s)
  })
  liveCatalogue.forEach((c) => {
    if (claimed.has(c)) return
    const s = claim(c, true)
    if (s) claimed.set(c, s)
  })
  const merged = liveCatalogue.map((c) => {
    const s = claimed.get(c) || {}
    return {
      ...s,
      // The reference id exactly as the catalogue writes it — deliveries file
      // stock under "AA_5P_139", and a lower-cased key here made a stock check
      // file the same item as "aa_5p_139": two inventory rows (2026-09-17).
      item_key:
        s.item_key || String(c.reference_id || '').trim() || norm(c.item_name),
      item_name: c.item_name || s.item_name,
      segment: c.segment || '',
      category: c.category || s.category || '',
      reference_id: c.reference_id || s.reference_id || '',
      reference_link: c.reference_link || '',
      file_link: c.file_link || '',
      unit: c.unit || s.unit || '',
      item_type: c.item_type || '',
      required: Number(c.default_qty) || 0,
      on_hand: Number(s.on_hand) || 0,
      inCatalogue: true
    }
  })
  const strays = allItems
    .filter((i) => !matched.has(i.item_key))
    .map((i) => ({
      ...i,
      segment: '',
      item_type: '',
      required: 0,
      on_hand: Number(i.on_hand) || 0,
      inCatalogue: false
    }))
  const allRows = [...merged, ...strays]

  const has = (val, q) =>
    !q || String(val || '').toLowerCase().includes(q.toLowerCase())
  // A gap only means something for one location — "all" is a rollup across them.
  const isGap = (i) => !isAll && i.required > 0 && i.on_hand < i.required
  const items = allRows.filter(
    (i) =>
      has(i.segment, colFilters.segment) &&
      has(i.item_name, colFilters.item_name) &&
      has(i.reference_id, colFilters.reference_id) &&
      has(i.category, colFilters.category) &&
      has(i.item_type, colFilters.item_type) &&
      // ⛔ Both "only gaps" and "only stock" are OFF while counting, whatever
      // the buttons say. A stock check that only lists what Alfred already
      // believes is held can never find the thing Alfred does not know about —
      // and finding those is the whole reason for walking the shelf.
      (counting || !gapsOnly || isGap(i)) &&
      (counting || !stockOnly || Number(i.on_hand) > 0)
  )
  const gapCount = allRows.filter(isGap).length
  // Counted off allRows like gapCount, so the number on the button says how much
  // stock exists here, not how much survives whatever is typed in the headers.
  const stockCount = allRows.filter((i) => Number(i.on_hand) > 0).length
  // Sticky offsets and the table width both come off the same widths as the
  // <colgroup>, so they cannot drift apart.
  const segLeft = isAll ? 0 : CB_W
  const itemLeft = segLeft + SEG_W
  // Every column's width, derived from the SAME display sequence the header and
  // the body map over, so a folded band cannot shift the <colgroup> out of step
  // with the cells. A stub is 10px wide wherever it lands.
  const colWidths = displayCols.map((d) =>
    d.kind === 'stub' ? STUB_W : COL_W[d.col.k] || 100
  )
  // ONE column is left without a declared width so it absorbs the table's
  // slack — under `table-layout: fixed` the columns that declare a width get
  // exactly it and the rest share what is left, which is what stops the table
  // ending in a sea of white AND what keeps the frozen columns on the exact
  // widths the sticky offsets are computed from. Normally that is Category.
  // ⭐ Fold Item detail away and Category goes with it, leaving nothing but
  // right-aligned numbers — and handing the slack to one of THOSE parked the
  // count 700px away from the item it belongs to. So when the band is folded a
  // trailing empty column takes the slack instead, and the numbers stay beside
  // the item. Every row has to carry that spacer cell or the header and the
  // body drift apart, exactly as they would for a mis-mapped band.
  const absorbIdx = displayCols.findIndex((d) => d.kind === 'col' && d.col.flex)
  const spacer = absorbIdx < 0
  // The floor below which the table scrolls sideways rather than squeezing.
  const tableW =
    itemLeft + ITEM_W + colWidths.reduce((a, b) => a + b, 0)
  // Per-segment rollup for the group headers. The requests grid's header says
  // how many lines a segment holds; here the question a person actually opens
  // this tab with is "is this game's kit complete at this apartment", so the
  // header answers THAT — held / short / not received — and falls back to a
  // plain unit total on the "all apartments" rollup, where a gap has no meaning.
  const groupStats = React.useMemo(() => {
    const by = {}
    items.forEach((i) => {
      const key = String(i.segment || '').trim() || '(no segment)'
      const g =
        by[key] ||
        (by[key] = { items: 0, units: 0, complete: 0, short: 0, none: 0 })
      g.items += 1
      g.units += Number(i.on_hand) || 0
      if (!isAll && i.required > 0) {
        if (i.on_hand >= i.required) g.complete += 1
        else if (i.on_hand <= 0) g.none += 1
        else g.short += 1
      }
    })
    return by
  }, [items, isAll])

  const ledger = (data && data.ledger) || []
  // ⭐ The answer to "has this apartment actually been counted?". It cannot come
  // from the movement log: a count that agreed with the book writes no movement,
  // so a perfect count and no count at all look identical there.
  const lastCount = (data && data.last_count) || null
  const here = locations.find(
    (l) => l.uuid === scope || (scope === 'hq' && l.is_hq)
  )
  const hereName = here ? here.name : ''
  // Where stock can be sent: any location except the one we're viewing.
  const otherLocations = locations.filter((l) => !here || l.uuid !== here.uuid)
  // Bulk transfer works on the items that actually have stock to send.
  const transferable = items.filter((i) => Number(i.on_hand) > 0)
  const bselItems = transferable.filter((i) => bsel.has(i.item_key))
  const allBselSelected =
    transferable.length > 0 && transferable.every((i) => bsel.has(i.item_key))
  const toggleBselAll = () =>
    setBsel(allBselSelected ? new Set() : new Set(transferable.map((i) => i.item_key)))

  // ------------------------------------------------------------- stock check
  // Only the rows actually typed into count. A blank is "not counted", NOT
  // zero — the difference matters: an apartment gets through 40 items of 372
  // in one sitting and the other 332 must stay untouched.
  const countedRows = counting
    ? items.filter((i) => String(counts[i.item_key] ?? '').trim() !== '')
    : []
  const countDiff = (i) =>
    Number(counts[i.item_key]) - Number(i.on_hand || 0)
  const movedRows = countedRows.filter((i) => countDiff(i) !== 0)
  const needReason = movedRows.filter(
    (i) => !String(reasons[i.item_key] || '').trim()
  )

  const submitCount = async () => {
    setCountErr('')
    if (!countedRows.length) return
    if (needReason.length) {
      return setCountErr(
        `${needReason.length} counted item${needReason.length === 1 ? '' : 's'} ` +
          'came out different from what Alfred holds — say why before saving.'
      )
    }
    setBusy(true)
    try {
      const res = await saveAptStockCheck({
        apartment: here ? here.uuid : 'hq',
        note: countNote,
        counts: countedRows.map((i) => ({
          item_key: i.item_key,
          item_name: i.item_name,
          unit: i.unit || '',
          counted: Number(counts[i.item_key]),
          reason: reasons[i.item_key] || ''
        }))
      })
      notify(
        `Stock check saved — ${res.counted} counted, ${res.differed} corrected`,
        '',
        'success'
      )
      setCounts({})
      setReasons({})
      setCountNote('')
      load(scope)
    } catch (e) {
      setCountErr((e && e.message) || 'Could not save the stock check.')
    } finally {
      setBusy(false)
    }
  }

  const submitSend = async () => {
    const qty = Number(send.qty)
    if (!(qty > 0)) return setSend((s) => ({ ...s, error: 'Enter a quantity.' }))
    if (!send.to) return setSend((s) => ({ ...s, error: 'Pick a destination.' }))
    if (qty > Number(send.item.on_hand)) {
      return setSend((s) => ({ ...s, error: `Only ${send.item.on_hand} on hand.` }))
    }
    setBusy(true)
    try {
      await allocateAptInventory({
        item_key: send.item.item_key,
        qty,
        from_apartment: here ? here.uuid : 'hq',
        to_apartment: send.to
      })
      notify('Stock sent', '', 'success')
      setSend(null)
      load(scope)
    } catch (e) {
      setSend((s) => ({ ...s, error: (e && e.message) || 'Could not send.' }))
    } finally {
      setBusy(false)
    }
  }

  const submitUse = async () => {
    const qty = Number(use.qty)
    if (!(qty > 0)) return setUse((u) => ({ ...u, error: 'Enter a quantity.' }))
    if (qty > Number(use.item.on_hand)) {
      return setUse((u) => ({ ...u, error: `Only ${use.item.on_hand} on hand.` }))
    }
    setBusy(true)
    try {
      await consumeAptInventory({
        item_key: use.item.item_key,
        qty,
        apartment: here ? here.uuid : 'hq',
        note: use.note
      })
      notify('Usage recorded', '', 'success')
      setUse(null)
      load(scope)
    } catch (e) {
      setUse((u) => ({ ...u, error: (e && e.message) || 'Could not record usage.' }))
    } finally {
      setBusy(false)
    }
  }

  const submitDelete = async () => {
    setBusy(true)
    try {
      await deleteAptInventoryItem(del.item.item_key)
      notify('Item removed from inventory', '', 'success')
      setDel(null)
      load(scope)
    } catch (e) {
      notify((e && e.message) || 'Could not delete.', '', 'error')
    } finally {
      setBusy(false)
    }
  }

  const toggleBsel = (key) =>
    setBsel((p) => {
      const n = new Set(p)
      if (n.has(key)) n.delete(key)
      else n.add(key)
      return n
    })
  // Bulk transfer sends each selected item's FULL on-hand to one apartment.
  const submitBulkTransfer = async () => {
    if (!bulkTo || !bselItems.length) return
    setBusy(true)
    try {
      // eslint-disable-next-line no-restricted-syntax
      for (const it of bselItems) {
        // eslint-disable-next-line no-await-in-loop
        await allocateAptInventory({
          item_key: it.item_key,
          qty: Number(it.on_hand),
          from_apartment: here ? here.uuid : 'hq',
          to_apartment: bulkTo
        })
      }
      notify(`Transferred ${bselItems.length} items`, '', 'success')
      setBsel(new Set())
      setBulkOpen(false)
      setBulkTo('')
      load(scope)
    } catch (e) {
      notify((e && e.message) || 'Could not transfer.', '', 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Box className={classes.root}>
      <Typography variant="body2" className={classes.lede}>
        Stock across <b>every apartment</b> and the <b>At Apartment HQ</b> store.
        Delivered items stock into their apartment automatically; <b>send</b> stock
        between locations and record what's <b>used</b>. On hand = received +
        transfers in − transfers out − used.
      </Typography>

      <Box className={classes.bar}>
        <TextField
          select
          size="small"
          variant="outlined"
          label="Location"
          className={classes.picker}
          value={scope}
          onChange={(e) => setScope(e.target.value)}>
          <MenuItem value="all">All apartments</MenuItem>
          {locations.map((l) => (
            <MenuItem key={l.uuid} value={l.uuid}>
              {l.is_hq ? `${l.name} (store)` : l.name}
            </MenuItem>
          ))}
        </TextField>
        {isAll && (
          <Typography variant="caption" className={classes.low}>
            Pick a location to send stock or record usage.
          </Typography>
        )}
        {/* Both filters stay in the bar during a stock check, invisible, so the
            bar keeps its width: removing them let the bar re-wrap and the
            On hand / Stock check toggle jumped a line up and back down on
            every switch (Satya, 2026-09-17). */}
        {!isAll && (
          <Button
            size="small"
            style={counting ? { visibility: 'hidden' } : undefined}
            aria-hidden={counting || undefined}
            tabIndex={counting ? -1 : undefined}
            variant={gapsOnly ? 'contained' : 'outlined'}
            color={gapsOnly ? 'primary' : 'default'}
            disableElevation
            title="Only the items this location holds less of than the catalogue asks for"
            onClick={() => setGapsOnly((g) => !g)}>
            {gapsOnly ? `Showing ${gapCount} gaps` : `Only show gaps (${gapCount})`}
          </Button>
        )}
        {/* Unlike gaps, this reads the same for one location or the rollup —
            "what is actually held" is meaningful either way. */}
        <Button
          size="small"
          style={counting ? { visibility: 'hidden' } : undefined}
          aria-hidden={counting || undefined}
          tabIndex={counting ? -1 : undefined}
          variant={stockOnly ? 'contained' : 'outlined'}
          color={stockOnly ? 'primary' : 'default'}
          disableElevation
          title="Only the items with something on hand — hides every zero row"
          onClick={() => setStockOnly((s) => !s)}
        >
          {stockOnly
            ? `Showing ${stockCount} in stock`
            : `Only show stock (${stockCount})`}
        </Button>
        {/* The same grouping the requests grid carries, in the same words —
            the catalogue's own segments, which is how a kit is thought about
            and walked. Off by default there and here: the two grids are read
            by the same people and a toggle cannot mean different things. */}
        <Button
          size="small"
          className={classes.bandChip}
          variant={grouped ? 'contained' : 'outlined'}
          color={grouped ? 'primary' : 'default'}
          disableElevation
          title="Group the items by segment — the catalogue's own grouping, the same way the requests grid groups them"
          onClick={toggleGrouped}>
          Group by segment
        </Button>
        {grouped && (
          <>
            <Button size="small" className={classes.bandChip} onClick={expandAll}>
              Expand all
            </Button>
            <Button
              size="small"
              className={classes.bandChip}
              onClick={() => collapseAllOf(items)}>
              Collapse all
            </Button>
          </>
        )}
        <Box flexGrow={1} />
        <Box className={classes.toggle}>
          <Button
            size="small"
            disableElevation
            variant={view === 'stock' ? 'contained' : 'text'}
            color={view === 'stock' ? 'primary' : 'default'}
            onClick={() => setView('stock')}>
            On hand
          </Button>
          <Button
            size="small"
            disableElevation
            variant={view === 'count' ? 'contained' : 'text'}
            color={view === 'count' ? 'primary' : 'default'}
            title="Count what is physically at this apartment and correct what Alfred holds"
            onClick={() => setView('count')}>
            Stock check
          </Button>
          <Button
            size="small"
            disableElevation
            variant={view === 'log' ? 'contained' : 'text'}
            color={view === 'log' ? 'primary' : 'default'}
            onClick={() => setView('log')}>
            Movement log
          </Button>
        </Box>
      </Box>

      {loading && <LinearProgress />}

      {/* A stock check is not a different screen — it is the same grid with a
          column to type into, so the grouping and the folding a person set up
          to read their stock are the ones they walk the shelf with. */}
      {view === 'count' && isAll && (
        <Box
          style={{
            margin: '16px 0',
            padding: 16,
            border: '1px solid #E4CFC6',
            background: '#FBEDE8',
            borderRadius: 4
          }}>
          <Typography variant="body2">
            A stock check is done <b>at one apartment</b> — pick the location
            above, then count what is actually on its shelves.
          </Typography>
        </Box>
      )}

      {(view === 'stock' || counting) && (
      <>
      <Typography variant="subtitle2" className={classes.h}>
        {counting ? 'Stock check' : 'On hand'}{here ? ` · ${hereName}` : ''}
        {lastCount && (
          <span className={classes.lastCount}>
            {`last counted ${lastCount.date}`}
            {lastCount.by ? ` by ${lastCount.by}` : ''}
            {` · ${lastCount.counted} item${
              lastCount.counted === 1 ? '' : 's'
            }, ${lastCount.differed} corrected`}
          </span>
        )}
        {!lastCount && !isAll && (
          <span className={classes.lastCount}>never counted</span>
        )}
      </Typography>
      {counting && (
        <Typography variant="body2" className={classes.lede}>
          Type what is <b>actually</b> on the shelf. Blank means not counted yet —
          the walk can be done over several sittings. Anything that comes out
          different from what Alfred holds needs a reason, and nothing is written
          until <b>Save</b>.
        </Typography>
      )}
      {!isAll && !counting && bselItems.length > 0 && (
        <Box
          style={{
            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
            padding: 8, marginBottom: 8, background: '#eef4ff', borderRadius: 4
          }}>
          <Typography variant="body2">
            <b>{bselItems.length}</b> item{bselItems.length === 1 ? '' : 's'} selected
          </Typography>
          <Button
            size="small"
            variant="contained"
            color="primary"
            disableElevation
            disabled={busy}
            onClick={() => setBulkOpen(true)}>
            Transfer {bselItems.length} to an apartment
          </Button>
          <Button size="small" onClick={() => setBsel(new Set())}>Clear</Button>
        </Box>
      )}
      {/* No scroll box. An `overflow` of anything but `visible` makes this the
          scrollport, and then the header only sticks INSIDE it — scroll the
          page and the whole 65vh box, header and all, slides away. Letting the
          document scroll instead is what HqMonthlyBoard does, and it is why its
          header and frozen columns hold against the window. */}
      <TableContainer
        component={Paper}
        variant="outlined"
        style={{ overflow: 'visible' }}>
        <Table
          size="small"
          stickyHeader
          className={classes.striped}
          style={{ tableLayout: 'fixed', width: '100%', minWidth: tableW }}>
          <colgroup>
            {!isAll && <col style={{ width: CB_W }} />}
            <col style={{ width: SEG_W }} />
            <col style={{ width: ITEM_W }} />
            {/* Built from the display sequence, so a folded band takes its
                10px stub here too and the columns stay in step with the
                cells. One column is left width-less to absorb the slack. */}
            {displayCols.map((d, i) => (
              <col
                key={d.kind === 'stub' ? `w-stub-${d.band.id}` : `w-${d.col.k}`}
                style={i === absorbIdx ? undefined : { width: colWidths[i] }}
              />
            ))}
            {spacer && <col />}
          </colgroup>
          <TableHead>
            {/* The orange band row. Clicking a band folds its run away; the
                fold leaves a clickable seam rather than silently dropping
                columns, which on an operational grid would read as missing
                data. */}
            <TableRow>
              {!isAll && <TableCell padding="checkbox" className={classes.bandRowSpacer} />}
              <TableCell colSpan={2} className={classes.bandRowSpacer} />
              {bandHeaderRow(displayCols, classes, toggleBand, bandOf)}
              {spacer && <TableCell className={classes.bandRowSpacer} />}
            </TableRow>
            <TableRow>
              {!isAll && (
                <TableCell
                  padding="checkbox"
                  className={`${classes.freeze} ${classes.freezeHead}`}
                  style={{ left: 0 }}>
                  {/* Transfer selection has no place on a count sheet. */}
                  {!counting && (
                    <Checkbox
                      size="small"
                      checked={allBselSelected}
                      indeterminate={bselItems.length > 0 && !allBselSelected}
                      onChange={toggleBselAll}
                    />
                  )}
                </TableCell>
              )}
              <TableCell
                className={`${classes.headCell} ${classes.freeze} ${classes.freezeHead}`}
                style={{ left: segLeft }}>
                <TextField
                  className={classes.headInput}
                  size="small"
                  variant="outlined"
                  placeholder="segment"
                  value={colFilters.segment}
                  onChange={(e) => {
                    const v = e.target.value
                    setColFilters((f) => ({ ...f, segment: v }))
                  }}
                />
              </TableCell>
              <TableCell
                className={`${classes.headCell} ${classes.freeze} ${classes.freezeHead} ${classes.freezeEdge}`}
                style={{ left: itemLeft }}>
                <TextField
                  className={classes.headInput}
                  size="small"
                  variant="outlined"
                  placeholder="item"
                  value={colFilters.item_name}
                  onChange={(e) => {
                    const v = e.target.value
                    setColFilters((f) => ({ ...f, item_name: v }))
                  }}
                />
              </TableCell>
              {/* ⛔ Every row in this table — header, group, body, empty —
                  goes through mapBandedCells, or a folded band shifts one
                  row's cells and not another's. */}
              {mapBandedCells(displayCols, classes, toggleBand, (c) => (
                <TableCell
                  key={c.k}
                  className={`${classes.headCell}${c.num ? ` ${classes.num}` : ''}`}
                  align={c.num || c.k === 'actions' ? 'right' : 'inherit'}>
                  {c.ph ? (
                    <TextField
                      className={classes.headInput}
                      size="small"
                      variant="outlined"
                      placeholder={c.ph}
                      value={colFilters[c.k] || ''}
                      onChange={(e) => {
                        const v = e.target.value
                        setColFilters((f) => ({ ...f, [c.k]: v }))
                      }}
                    />
                  ) : (
                    <span className={classes.headLabel}>{c.label}</span>
                  )}
                </TableCell>
              ))}
              {spacer && <TableCell className={classes.headCell} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {groupEntries(items, grouped, collapsedGroups).map((entry) =>
              entry.kind === 'group' ? (
                <TableRow
                  key={`grp-${entry.key}`}
                  className={classes.groupRow}
                  onClick={() => toggleGroup(entry.key)}>
                  {!isAll && (
                    <TableCell
                      padding="checkbox"
                      className={classes.freeze}
                      style={{ left: 0 }}>
                      <span style={{ color: '#8a857c', fontSize: 11 }}>
                        {entry.open ? '▾' : '▸'}
                      </span>
                    </TableCell>
                  )}
                  <TableCell className={classes.freeze} style={{ left: segLeft }}>
                    <span className={classes.wrapCell} title={entry.name}>
                      {isAll && (
                        <span style={{ color: '#8a857c', fontSize: 11, marginRight: 6 }}>
                          {entry.open ? '▾' : '▸'}
                        </span>
                      )}
                      {entry.name}
                    </span>
                  </TableCell>
                  <TableCell
                    className={`${classes.freeze} ${classes.freezeEdge}`}
                    style={{ left: itemLeft }}>
                    <span className={classes.groupMeta}>
                      {entry.count} item{entry.count === 1 ? '' : 's'}
                    </span>
                  </TableCell>
                  {mapBandedCells(displayCols, classes, toggleBand, (c) => {
                    const g = groupStats[entry.key] || {}
                    // The rollup rides in On hand and Status, the two columns
                    // it is actually about — not in a colSpan banner, which
                    // would break the fixed layout the freeze depends on.
                    if (c.k === 'on_hand') {
                      return (
                        <TableCell key={c.k} className={`${classes.num} ${classes.groupCount}`} align="right">
                          {g.units || 0}
                        </TableCell>
                      )
                    }
                    if (c.k === 'status') {
                      const bits = []
                      if (g.complete) bits.push(`${g.complete} complete`)
                      if (g.short) bits.push(`${g.short} short`)
                      if (g.none) bits.push(`${g.none} not received`)
                      return (
                        <TableCell key={c.k} style={{ whiteSpace: 'nowrap' }}>
                          <span className={classes.groupMeta} style={{ marginLeft: 0 }}>
                            {bits.join(' · ') || '—'}
                          </span>
                        </TableCell>
                      )
                    }
                    return <TableCell key={c.k} />
                  })}
                  {spacer && <TableCell />}
                </TableRow>
              ) : (
                ((it) => (
              <TableRow key={it.item_key} hover>
                {!isAll && (
                  <TableCell
                    padding="checkbox"
                    className={classes.freeze}
                    style={{ left: 0 }}>
                    {Number(it.on_hand) > 0 && !counting && (
                      <Checkbox
                        size="small"
                        checked={bsel.has(it.item_key)}
                        onChange={() => toggleBsel(it.item_key)}
                      />
                    )}
                  </TableCell>
                )}
                <TableCell
                  className={classes.freeze}
                  style={{ left: segLeft }}>
                  {/* Grouped, the header above already names the segment —
                      repeating it on every row under it reads as noise. The
                      cell stays (the column is frozen and its width is what
                      the sticky offsets are built from), it just goes quiet. */}
                  <span className={classes.wrapCell} title={it.segment || ''}>
                    {grouped ? '' : it.segment || '—'}
                  </span>
                </TableCell>
                <TableCell
                  className={`${classes.freeze} ${classes.freezeEdge}`}
                  style={{ left: itemLeft }}>
                  <span className={classes.wrapCell} title={it.item_name || ''}>
                    {it.item_name}
                    {it.reference_link && (
                      <a
                        className={classes.linkArrow}
                        href={it.reference_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open the item">
                        ↗
                      </a>
                    )}
                    {/* Second file of a two-PDF item (2026-09-17). */}
                    {it.file_link && (
                      <a
                        className={classes.linkArrow}
                        href={it.file_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open the second file">
                        ↗
                      </a>
                    )}
                  </span>
                </TableCell>
                {mapBandedCells(displayCols, classes, toggleBand, (c) => {
                  if (c.k === 'required') {
                    return (
                      <TableCell key={c.k} className={classes.num} align="right">
                        {it.required || '—'}
                      </TableCell>
                    )
                  }
                  if (c.k === 'on_hand') {
                    return (
                      <TableCell key={c.k} className={classes.num} align="right">
                        <b>{Number(it.on_hand)}</b>
                      </TableCell>
                    )
                  }
                  if (c.k === 'status') {
                    return (
                      <TableCell key={c.k}>
                        {(() => {
                          if (!it.required) return '—'
                          if (it.on_hand >= it.required)
                            return (
                              <span className={`${classes.gapPill} ${classes.pillOk}`}>
                                complete
                              </span>
                            )
                          if (it.on_hand <= 0)
                            return (
                              <span className={`${classes.gapPill} ${classes.pillNone}`}>
                                not received
                              </span>
                            )
                          return (
                            <span className={`${classes.gapPill} ${classes.pillShort}`}>
                              {`short ${it.required - it.on_hand}`}
                            </span>
                          )
                        })()}
                      </TableCell>
                    )
                  }
                  if (c.k === 'counted') {
                    return (
                      <TableCell key={c.k} align="right">
                        <TextField
                          className={classes.countInput}
                          size="small"
                          variant="outlined"
                          type="number"
                          placeholder="—"
                          inputProps={{ min: 0 }}
                          value={counts[it.item_key] ?? ''}
                          onChange={(e) => {
                            // ⭐ React 16 pools the event, so the value must be
                            // read BEFORE the updater runs.
                            const v = e.target.value
                            setCounts((p) => ({ ...p, [it.item_key]: v }))
                          }}
                        />
                      </TableCell>
                    )
                  }
                  if (c.k === 'difference') {
                    const typed = String(counts[it.item_key] ?? '').trim()
                    if (!typed) {
                      return (
                        <TableCell key={c.k} className={classes.num} align="right">
                          <span className={classes.diffSame}>—</span>
                        </TableCell>
                      )
                    }
                    const d = countDiff(it)
                    return (
                      <TableCell key={c.k} className={classes.num} align="right">
                        {d === 0 ? (
                          <span className={classes.diffSame}>matches</span>
                        ) : (
                          <span className={d > 0 ? classes.diffUp : classes.diffDown}>
                            {d > 0 ? `+${d}` : d}
                          </span>
                        )}
                      </TableCell>
                    )
                  }
                  if (c.k === 'count_reason') {
                    const typed = String(counts[it.item_key] ?? '').trim()
                    const moved = typed && countDiff(it) !== 0
                    if (!moved) {
                      return (
                        <TableCell key={c.k}>
                          <span className={classes.diffSame}>—</span>
                        </TableCell>
                      )
                    }
                    return (
                      <TableCell key={c.k}>
                        <TextField
                          select
                          className={classes.headInput}
                          size="small"
                          variant="outlined"
                          fullWidth
                          error={!reasons[it.item_key]}
                          value={reasons[it.item_key] || ''}
                          onChange={(e) => {
                            const v = e.target.value
                            setReasons((p) => ({ ...p, [it.item_key]: v }))
                          }}>
                          {COUNT_REASONS.map((r) => (
                            <MenuItem key={r} value={r}>
                              {r}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                    )
                  }
                  if (c.k === 'actions') {
                    return (
                      <TableCell key={c.k} align="right" style={{ whiteSpace: 'nowrap' }}>
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          disableElevation
                          disabled={Number(it.on_hand) <= 0}
                          onClick={() => setSend({ item: it, qty: '', to: '', error: '' })}>
                          Transfer to apartment
                        </Button>
                        <Button
                          size="small"
                          style={{ marginLeft: 6, color: '#607d8b' }}
                          disabled={Number(it.on_hand) <= 0}
                          onClick={() => setUse({ item: it, qty: '', note: '', error: '' })}>
                          Record usage
                        </Button>
                        {canScrap && (
                          <Button
                            size="small"
                            style={{ marginLeft: 6, color: '#c62828' }}
                            onClick={() => setDel({ item: it })}>
                            Scrap
                          </Button>
                        )}
                      </TableCell>
                    )
                  }
                  return (
                    <TableCell
                      key={c.k}
                      title={it[c.k] || ''}>
                      {/* Category is prose and wraps; a reference id is one
                          token and must NOT — `overflowWrap: anywhere` split
                          "aa_5p_089" across two lines, which is unreadable and
                          uncopyable. Its column is wide enough to hold it. */}
                      {c.k === 'category' ? (
                        <span className={classes.wrapCell}>{it[c.k] || '—'}</span>
                      ) : (
                        it[c.k] || '—'
                      )}
                    </TableCell>
                  )
                })}
                {spacer && <TableCell />}
              </TableRow>
                ))(entry.row)
              )
            )}
            {!items.length && !loading && (
              <TableRow>
                <TableCell colSpan={(isAll ? 2 : 3) + displayCols.length + (spacer ? 1 : 0)}>
                  <Typography variant="body2" className={classes.low}>
                    No stock here yet — items appear once a delivered line is marked
                    delivered for this apartment, or stock is sent to it.
                  </Typography>
                  {stockOnly && allRows.length > 0 && (
                    <Button
                      size="small"
                      color="primary"
                      onClick={() => setStockOnly(false)}>
                      Show all {allRows.length} catalogue items
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {counting && (
        <Box className={classes.countBar}>
          <Typography variant="body2">
            <b>{countedRows.length}</b> of {items.length} counted
            {movedRows.length > 0 && (
              <>
                {' · '}
                <b>{movedRows.length}</b> differ
              </>
            )}
          </Typography>
          <TextField
            size="small"
            variant="outlined"
            placeholder="note for this check (optional)"
            className={classes.headInput}
            style={{ minWidth: 260 }}
            value={countNote}
            onChange={(e) => {
              const v = e.target.value
              setCountNote(v)
            }}
          />
          {countErr && (
            <Typography variant="caption" style={{ color: '#c62828' }}>
              {countErr}
            </Typography>
          )}
          <Box flexGrow={1} />
          {countedRows.length > 0 && (
            <Button
              size="small"
              disabled={busy}
              onClick={() => {
                setCounts({})
                setReasons({})
                setCountErr('')
              }}>
              Clear
            </Button>
          )}
          <Button
            size="small"
            variant="contained"
            color="primary"
            disableElevation
            disabled={busy || !countedRows.length || needReason.length > 0}
            title={
              needReason.length
                ? `${needReason.length} of the differences still needs a reason`
                : 'Write these counts to the inventory'
            }
            onClick={submitCount}>
            Save stock check
          </Button>
        </Box>
      )}
      </>
      )}

      {view === 'log' && (
      <>
      <Typography variant="subtitle2" className={classes.h}>
        Movement log{here ? ` · ${hereName}` : ''}
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Item</TableCell>
              <TableCell className={classes.num} align="right">Qty</TableCell>
              <TableCell>Movement</TableCell>
              <TableCell>From</TableCell>
              <TableCell>To</TableCell>
              <TableCell>By</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ledger.map((m, i) => (
              <TableRow key={i} hover>
                <TableCell>{m.date}</TableCell>
                <TableCell>{m.item_name}</TableCell>
                <TableCell className={classes.num} align="right">{Number(m.qty)}</TableCell>
                <TableCell>{REASON_LABEL[m.reason] || m.reason}</TableCell>
                <TableCell>{m.from || '—'}</TableCell>
                <TableCell>{m.to || '—'}</TableCell>
                <TableCell>{m.by || '—'}</TableCell>
              </TableRow>
            ))}
            {!ledger.length && !loading && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" className={classes.low}>
                    No movements yet.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      </>
      )}

      <Dialog open={bulkOpen} onClose={() => setBulkOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Transfer {bselItems.length} items to an apartment</DialogTitle>
        <DialogContent>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 4 }}>
            <Typography variant="body2">
              Each selected item's full on-hand quantity{hereName ? ` at ${hereName}` : ''} is
              sent to the apartment you pick.
            </Typography>
            <TextField
              select
              size="small"
              variant="outlined"
              label="To apartment"
              value={bulkTo}
              onChange={(e) => {
                const v = e.target.value
                setBulkTo(v)
              }}>
              {otherLocations.map((l) => (
                <MenuItem key={l.uuid} value={l.uuid}>
                  {l.is_hq ? `${l.name} (store)` : l.name}
                </MenuItem>
              ))}
            </TextField>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkOpen(false)} disabled={busy}>Cancel</Button>
          <Button color="primary" variant="contained" disableElevation
            disabled={busy || !bulkTo} onClick={submitBulkTransfer}>
            Transfer {bselItems.length}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!del} onClose={() => setDel(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove from inventory?</DialogTitle>
        <DialogContent>
          {del && (
            <Typography variant="body2">
              This permanently removes <b>{del.item.item_name}</b> and all its
              stock history from inventory (every location). This can't be undone —
              use it to scrap an item or clean data.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDel(null)} disabled={busy}>Cancel</Button>
          <Button style={{ color: '#fff', background: '#c62828' }} disableElevation
            variant="contained" disabled={busy} onClick={submitDelete}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!send} onClose={() => setSend(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Transfer to an apartment{hereName ? ` · from ${hereName}` : ''}</DialogTitle>
        <DialogContent>
          {send && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 4 }}>
              <Typography variant="body2">
                <b>{send.item.item_name}</b> · {Number(send.item.on_hand)}{' '}
                {send.item.unit || 'units'} on hand
              </Typography>
              <TextField
                type="number"
                size="small"
                variant="outlined"
                label="Quantity"
                value={send.qty}
                onChange={(e) => {
                  const v = e.target.value
                  setSend((s) => ({ ...s, qty: v }))
                }}
              />
              <TextField
                select
                size="small"
                variant="outlined"
                label="To apartment"
                value={send.to}
                onChange={(e) => {
                  const v = e.target.value
                  setSend((s) => ({ ...s, to: v }))
                }}>
                {otherLocations.map((l) => (
                  <MenuItem key={l.uuid} value={l.uuid}>
                    {l.is_hq ? `${l.name} (store)` : l.name}
                  </MenuItem>
                ))}
              </TextField>
              {send.error && (
                <Typography variant="caption" style={{ color: '#c62828' }}>
                  {send.error}
                </Typography>
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSend(null)} disabled={busy}>Cancel</Button>
          <Button color="primary" variant="contained" disableElevation disabled={busy} onClick={submitSend}>
            Transfer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!use} onClose={() => setUse(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Record usage{hereName ? ` · ${hereName}` : ''}</DialogTitle>
        <DialogContent>
          {use && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 4 }}>
              <Typography variant="body2">
                <b>{use.item.item_name}</b> · {Number(use.item.on_hand)}{' '}
                {use.item.unit || 'units'} on hand
              </Typography>
              <TextField
                type="number"
                size="small"
                variant="outlined"
                label="Quantity used"
                value={use.qty}
                onChange={(e) => {
                  const v = e.target.value
                  setUse((u) => ({ ...u, qty: v }))
                }}
              />
              <TextField
                size="small"
                variant="outlined"
                label="Note (optional)"
                value={use.note}
                onChange={(e) => {
                  const v = e.target.value
                  setUse((u) => ({ ...u, note: v }))
                }}
              />
              {use.error && (
                <Typography variant="caption" style={{ color: '#c62828' }}>
                  {use.error}
                </Typography>
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUse(null)} disabled={busy}>Cancel</Button>
          <Button color="primary" variant="contained" disableElevation disabled={busy} onClick={submitUse}>
            Record
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default InventoryTab
